import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Close, Flag } from '@mui/icons-material';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppButton } from '../../../shared/components/buttons/AppButton';
import offerDetailService from '../services/offerDetailService';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';

interface ReportOfferModalProps {
  open: boolean;
  onClose: () => void;
  offerId?: string;
  onReported?: () => void;
}

export default function ReportOfferModal({
  open,
  onClose,
  offerId,
  onReported,
}: Readonly<ReportOfferModalProps>) {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const [reason, setReason] = useState<'INCORRECT' | 'OFFENSIVE' | 'OTHER' | ''>('');
  const [otherText, setOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const otherValid = otherText.trim().length >= 10;

  useEffect(() => {
    if (!open) {
      setReason('');
      setOtherText('');
      setIsSubmitting(false);
    }
  }, [open]);

  const canContinue = useMemo(() => {
    if (!reason) return false;
    if (reason === 'OTHER') return otherValid && !isSubmitting;
    return !isSubmitting;
  }, [reason, otherValid, isSubmitting]);

  const handleSubmit = async () => {
    if (!offerId) return;
    if (!reason) return;
    setIsSubmitting(true);
    try {
      await offerDetailService.reportOffer(
        offerId,
        reason,
        reason === 'OTHER' ? otherText.trim() : undefined
      );

      showSnackbar(t('offerDetail.reportSuccess', { variant: 'success' }), { variant: 'success' });
      onClose();
      onReported?.();
    } catch (err) {
      console.error('Report failed', err);
      showSnackbar(t('offerDetail.reportError'), { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            px: 2.5,
            pt: 2,
            pb: 3,
          },
        },
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            {t('offerDetail.reportTitle')}
          </Typography>
          <IconButton aria-label={t('offerDetail.closeModal')} onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider />

        <Typography variant="body2" fontWeight={600}>
          {t('offerDetail.reportReasonsTitle')}
        </Typography>

        <RadioGroup value={reason} onChange={e => setReason(e.target.value as any)}>
          <FormControlLabel
            value="INCORRECT"
            control={<Radio />}
            label={t('offerDetail.reportReasonIncorrect')}
          />
          <FormControlLabel
            value="OFFENSIVE"
            control={<Radio />}
            label={t('offerDetail.reportReasonOffensive')}
          />
          <FormControlLabel
            value="OTHER"
            control={<Radio />}
            label={t('offerDetail.reportReasonOther')}
          />
        </RadioGroup>

        {reason === 'OTHER' && (
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={otherText}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setOtherText(e.target.value)}
            placeholder={t('offerDetail.reportOtherPlaceholder')}
            helperText={
              otherText && otherText.trim().length < 10
                ? t('offerDetail.reportOtherTooShort')
                : undefined
            }
          />
        )}

        <Box sx={{ bgcolor: 'info.light', borderRadius: 1, p: 1 }}>
          <Typography variant="body2" color="text.primary">
            {t('offerDetail.reportInfo')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <AppButton variant="outlined" fullWidth onClick={onClose}>
            {t('common.cancel')}
          </AppButton>
          <AppButton
            variant="primary"
            fullWidth
            startIcon={<Flag />}
            onClick={handleSubmit}
            disabled={!canContinue}
          >
            {t('offerDetail.reportContinue')}
          </AppButton>
        </Stack>
      </Stack>
    </Drawer>
  );
}
